// client/src/pages/doctors/DoctorMessages.jsx - COMPLETE UPDATED VERSION
import React, { useState, useEffect, useRef } from 'react';
import { useTenant } from '../../../context/TenantContext';
import FeatureWrapper from '../../../components/common/FeatureWrapper';
import chatService from '../../../services/chatService';
import doctorService from '../../../services/doctorService';
import '../../../styles/components/doctor/DoctorMessages.css';

import neurolexLogo from '../../../assets/images/Neurolex_Logo_New.png';

import journalIconUrl from '../../../assets/logo/message_icon.png';

// 🎥 VIDEO SESSION COMPONENTS
import VideoSessionInvitation from './VideoSessionInvitation';
import VideoSessionOverlay from './VideoSessionOverlay';
import CreateSessionModal from './CreateSessionModal';

const DoctorMessages = () => {
  const { currentTenant, getThemeStyles, platformName } = useTenant();
  const theme = getThemeStyles();
  
  // Existing state management
  const [loading, setLoading] = useState(true);
  const [patients, setPatients] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [streamClient, setStreamClient] = useState(null);
  const [currentChannel, setCurrentChannel] = useState(null);
  const [doctorInfo, setDoctorInfo] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);

  const [unreadMessages, setUnreadMessages] = useState(new Map()); // Track unread per patient
  
  // 🎥 VIDEO SESSION STATE
  const [showCreateSessionModal, setShowCreateSessionModal] = useState(false);
  const [activeVideoSession, setActiveVideoSession] = useState(null);
  const [sessionInvitations, setSessionInvitations] = useState(new Map());
  const [upcomingSessions, setUpcomingSessions] = useState([]);
  
  // Refs
  const messagesEndRef = useRef(null);
  const messageInputRef = useRef(null);

  const sortPatientsByLastMessage = (patientsArray) => {
  return [...patientsArray].sort((a, b) => {
    // Get the most recent timestamp for each patient
    const timeA = a.lastMessageTime 
      ? new Date(a.lastMessageTime) 
      : (a.lastAppointment ? new Date(a.lastAppointment) : new Date(0));
    
    const timeB = b.lastMessageTime 
      ? new Date(b.lastMessageTime) 
      : (b.lastAppointment ? new Date(b.lastAppointment) : new Date(0));
    
    // Sort descending (most recent first)
    return timeB - timeA;
  });
};

  // 🎥 ENHANCED: Better quick session request detection
  const detectQuickSessionRequest = (message) => {
    if (!message.text || message.user?.id === doctorInfo._id) {
      return false;
    }
    
    // Multiple detection patterns for robustness
    const quickSessionPatterns = [
      /quick\s+session\s+requested/i,
      /🎥.*quick.*session/i,
      /video.*session.*requested/i,
      /urgent.*session/i,
      /immediate.*session/i
    ];
    
    return quickSessionPatterns.some(pattern => pattern.test(message.text));
  };

  // 🎥 ENHANCED: Quick session request component
  const QuickSessionRequestMessage = ({ message, onAccept, onDecline }) => {
    const [responding, setResponding] = useState(false);
    
    const handleAccept = async () => {
      if (responding) return;
      setResponding(true);
      try {
        await onAccept(message);
      } finally {
        setResponding(false);
      }
    };
    
    const handleDecline = async () => {
      if (responding) return;
      setResponding(true);
      try {
        await onDecline(message);
      } finally {
        setResponding(false);
      }
    };
    
    return (
      <div className="quick-session-request-message">
        <div className="request-container">
          {/* Request Header */}
          <div className="request-header">
            <div className="request-icon-wrapper">
              <span className="request-icon">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2" ry="2"/></svg>
              </span>
            </div>
            <div className="request-title-section">
              <h4 className="request-title">Video Session Request</h4>
              <p className="request-subtitle">Patient is requesting an immediate session</p>
            </div>
            <div className="request-status">
              <span className="status-badge urgent">Urgent</span>
            </div>
          </div>
          
          {/* Request Content */}
          <div className="request-content">
            <div className="request-message-text">
              <span className="message-icon"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg></span>
              <span className="message-text">{message.text}</span>
            </div>
            
            <div className="request-details">
              <div className="detail-item">
                <span className="detail-icon"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg></span>
                <span className="detail-text">
                  From: {selectedPatient?.firstName} {selectedPatient?.lastName}
                </span>
              </div>
              <div className="detail-item">
                <span className="detail-icon"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg></span>
                <span className="detail-text">
                  Requested: {formatMessageTime(message.created_at)}
                </span>
              </div>
            </div>
          </div>
          
          {/* Action Buttons */}
          <div className="request-actions">
            <button 
              className="request-btn accept-btn"
              onClick={handleAccept}
              disabled={responding}
            >
              {responding ? (
                <>
                  <div className="loading-spinner small"></div>
                  <span>Accepting...</span>
                </>
              ) : (
                <>
                  <span className="btn-icon"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg></span>
                  <span className="btn-text">Accept Session</span>
                </>
              )}
            </button>
            
            <button 
              className="request-btn decline-btn"
              onClick={handleDecline}
              disabled={responding}
            >
              {responding ? (
                <>
                  <div className="loading-spinner small"></div>
                  <span>Declining...</span>
                </>
              ) : (
                <>
                  <span className="btn-icon"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg></span>
                  <span className="btn-text">Decline</span>
                </>
              )}
            </button>
          </div>
          
          {/* Request Footer */}
          <div className="request-footer">
            <div className="footer-info">
              <span className="info-icon"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg></span>
              <span className="info-text">
                Accepting will create an immediate 30-minute video session
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  useEffect(() => {
  const initializeMessaging = async () => {
    try {
      console.log('🚀 [DEBUG] Starting initializeMessaging...');
      setLoading(true);
      setError(null);
      
      // Get doctor profile
      console.log('🚀 [DEBUG] Getting doctor profile...');

      // Get user data from storage first
      const userData = localStorage.getItem('user') || sessionStorage.getItem('user');
      if (userData) {
        const user = JSON.parse(userData);
        setCurrentUser(user);
      }

      const profileResponse = await doctorService.getProfile();
      console.log('🚀 [DEBUG] Profile response:', profileResponse);

      const profile = profileResponse.data || profileResponse;
      console.log('🚀 [DEBUG] Extracted profile:', profile);

      // Include profile picture
      setDoctorInfo({
        ...profile,
        profilePicture: profile.profilePicture
      });

      setCurrentUser(prev => ({
        ...prev,
        ...profile,
        profilePicture: profile.profilePicture
      }));
      
      console.log('🔍 Initializing messaging for doctor:', profile.firstName, profile.lastName);
      
      // Initialize Stream Chat
      console.log('🚀 [DEBUG] About to generate chat token...');
      const chatToken = await chatService.generateChatToken();
      console.log('🚀 [DEBUG] Chat token generated:', chatToken ? 'SUCCESS' : 'FAILED');
      
      console.log('🚀 [DEBUG] About to initialize Stream Chat...');
      const client = await chatService.initializeStreamChat(
        profile._id,
        {
          name: `Dr. ${profile.firstName} ${profile.lastName}`,
          userType: 'doctor',
          specialty: profile.specialty || profile.specialization || 'General',
          title: profile.title || 'Dr.',
          profilePicture: profile.profilePicture
        },
        chatToken
      );
      console.log('🚀 [DEBUG] Stream Chat client result:', client ? 'SUCCESS' : 'FAILED');
        
      setStreamClient(client);
      console.log('✅ Stream Chat initialized for doctor');
      
    
       await loadPatientsWithProfile(profile, client);  
      
      // ✅ NEW: Set up global listeners for ALL channels
      await setupGlobalChannelListeners(profile, client);
      
      // 🎥 Load upcoming sessions after initialization
      await loadUpcomingSessions();
      
    } catch (error) {
      console.error('❌ Error initializing messaging:', error);
      setError('Failed to initialize messaging system. Please try refreshing the page.');
    } finally {
      setLoading(false);
    }
  };

  initializeMessaging();
  
  // Cleanup on unmount
  return () => {
    if (streamClient) {
      streamClient.disconnectUser();
    }
  };
}, []);

  // 🎥 Load upcoming sessions
  const loadUpcomingSessions = async () => {
    try {
      console.log('🎥 Loading upcoming sessions...');
      const response = await fetch('/api/chat/sessions/upcoming', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const result = await response.json();
        setUpcomingSessions(result.data || []);
        console.log(`✅ Loaded ${result.data?.length || 0} upcoming sessions`);
      }
    } catch (error) {
      console.error('❌ Error loading upcoming sessions:', error);
    }
  };

 const loadPatientsWithProfile = async (profile, client) => {
  try {
    console.log('🔍 Loading patients for messaging...');
    
    // Call the new backend endpoint that gets all associated patients
    const response = await fetch('/api/chat/doctor/patients', {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      }
    });
    
    if (!response.ok) {
      throw new Error('Failed to fetch patients');
    }
    
    const result = await response.json();
    
    if (result.success && result.data) {
      console.log(`✅ Loaded ${result.data.length} patients from backend`);
      
      // ✅ Use the passed client instead of streamClient state
      if (client) {
        console.log('🔍 Starting Stream Chat queries for last messages...');
        const unreadMap = new Map();
        const patientsWithMessageTime = [];
        
        for (const patient of result.data) {
          try {
            const channelId = `doctor_${patient._id}_${profile._id}`;
            
            console.log(`🔍 Querying channel for ${patient.firstName}: ${channelId}`);
            
            // ✅ Use client parameter
            const channels = await client.queryChannels({
              type: 'messaging',
              id: channelId,
              members: { $in: [profile._id] }
            }, 
            { last_message_at: -1 },
            { 
              limit: 1,
              message_limit: 10
            });
            
            if (channels.length > 0) {
              const channel = channels[0];
              
              console.log(`✅ Found channel for ${patient.firstName}`);
              
              // Get unread count
              const unreadCount = channel.countUnread();
              if (unreadCount > 0) {
                unreadMap.set(patient._id, unreadCount);
                console.log(`📬 Patient ${patient.firstName} has ${unreadCount} unread messages`);
              }
              
              // Get last message time
              const messages = channel.state.messages;
              console.log(`💬 Channel ${channelId} has ${messages.length} messages in state`);
              
              // ✅ FULL CODE: Last message preview logic
              if (messages && messages.length > 0) {
                const lastMessage = messages[messages.length - 1];
                patient.lastMessageTime = lastMessage.created_at;
                
                // ✅ NEW: Add last message preview
                const isFromDoctor = lastMessage.user?.id === profile._id;
                const messageText = lastMessage.text || '';
                const preview = messageText.length > 50 ? messageText.substring(0, 50) + '...' : messageText;
                
                patient.lastMessagePreview = isFromDoctor ? `You: ${preview}` : preview;
                patient.lastMessageIsFromDoctor = isFromDoctor;
                
                const minutesAgo = Math.floor((new Date() - new Date(lastMessage.created_at)) / (1000 * 60));
                console.log(`⏰ Last message from ${patient.firstName}: ${lastMessage.created_at} (${minutesAgo} minutes ago)`);
                console.log(`📝 Preview: "${patient.lastMessagePreview}"`);
              } else {
                console.log(`⚠️ No messages found in state for ${patient.firstName}`);
                // ✅ NEW: Default preview when no messages
                patient.lastMessagePreview = 'No messages yet';
                patient.lastMessageIsFromDoctor = false;
              }
            } else {
              console.log(`⚠️ No channel found for ${patient.firstName}`);
              // ✅ Default values when no channel exists
              patient.lastMessagePreview = 'No messages yet';
              patient.lastMessageIsFromDoctor = false;
            }
            
            patientsWithMessageTime.push(patient);
            
          } catch (error) {
            console.log(`⏭️ Error checking ${patient.firstName}:`, error.message);
            // ✅ Default values on error
            patient.lastMessagePreview = 'No messages yet';
            patient.lastMessageIsFromDoctor = false;
            patientsWithMessageTime.push(patient);
          }
        }
        
        console.log('🔍 Finished Stream Chat queries');
        
        // Sort by last message time
        patientsWithMessageTime.sort((a, b) => {
          const timeA = a.lastMessageTime ? new Date(a.lastMessageTime) : new Date(a.lastAppointment);
          const timeB = b.lastMessageTime ? new Date(b.lastMessageTime) : new Date(b.lastAppointment);
          return timeB - timeA;
        });
        
        setPatients(patientsWithMessageTime);
        setUnreadMessages(unreadMap);
        
        console.log('✅ Patients sorted by last message time');
      } else {
        console.log('⚠️ No Stream client available, skipping message time queries');
        setPatients(result.data);
      }
    } else {
      console.error('❌ Invalid response format:', result);
      setPatients([]);
    }
    
  } catch (error) {
    console.error('❌ Error loading patients:', error);
    setError('Failed to load patients. Please try again.');
    setPatients([]);
  }
};

const setupGlobalChannelListeners = async (profile, client) => {
  try {
    console.log('🔔 Setting up global channel listeners for all patients...');
    
    // Query all channels where doctor is a member
    const channels = await client.queryChannels({
      type: 'messaging',
      members: { $in: [profile._id] }
    });
    
    console.log(`✅ Found ${channels.length} channels to monitor`);
    
    // Set up listener for each channel
    channels.forEach(channel => {
      channel.on('message.new', (event) => {
        // Extract patient ID from channel
        const channelId = channel.id;
        const patientId = channelId.replace(`doctor_`, '').replace(`_${profile._id}`, '');
        
        console.log('📩 Global listener: New message in channel:', channelId);
        
        // Only process if message is from patient (not from doctor)
        if (event.message.user?.id !== profile._id) {
          // Update unread count
          setUnreadMessages(prev => {
            const newMap = new Map(prev);
            const currentCount = newMap.get(patientId) || 0;
            newMap.set(patientId, currentCount + 1);
            return newMap;
          });
          
          // Update patient list with new message info
          setPatients(prevPatients => {
            const updatedPatients = prevPatients.map(p => {
              if (p._id === patientId) {
                const messageText = event.message.text || '';
                const preview = messageText.length > 50 ? messageText.substring(0, 50) + '...' : messageText;
                
                return {
                  ...p,
                  lastMessageTime: event.message.created_at,
                  lastMessagePreview: preview,
                  lastMessageIsFromDoctor: false
                };
              }
              return p;
            });
            
            // Sort by most recent message
            return sortPatientsByLastMessage(updatedPatients);
          });
          
          console.log('✅ Patient list reordered after new message from:', patientId);
        }
      });
    });
    
    console.log('✅ Global channel listeners set up successfully');
    
  } catch (error) {
    console.error('❌ Error setting up global channel listeners:', error);
  }
};


  // Filter patients based on search
  const filteredPatients = patients.filter(patient => {
    if (!searchTerm) return true;
    const fullName = `${patient.firstName} ${patient.lastName}`.toLowerCase();
    return fullName.includes(searchTerm.toLowerCase());
  });

  // Enhanced selectPatient to handle video sessions
const getDemoMessages = (patient, doctorId) => {
  const now = Date.now();
  const ts = (minsAgo) => new Date(now - minsAgo * 60000).toISOString();
  const drName = doctorInfo ? `Dr. ${doctorInfo.lastName}` : 'Doctor';
  const patName = patient ? patient.firstName : 'Patient';
  return [
    { id: 'demo-1', text: `Hi ${drName}, I've been practising the mindfulness exercises you recommended. They've been really helpful.`, user: { id: patient._id, name: `${patient.firstName} ${patient.lastName}` }, created_at: ts(2880) },
    { id: 'demo-2', text: `That's great to hear, ${patName}! Consistency is key. How have your sleep patterns been since we last spoke?`, user: { id: doctorId, name: drName }, created_at: ts(2875) },
    { id: 'demo-3', text: `Much better! I'm falling asleep faster and waking up more rested.`, user: { id: patient._id, name: `${patient.firstName} ${patient.lastName}` }, created_at: ts(2870) },
    { id: 'demo-4', text: `Excellent progress. Keep noting your sleep quality in your journal so we can track the trend. Are you still experiencing morning anxiety?`, user: { id: doctorId, name: drName }, created_at: ts(2865) },
    { id: 'demo-5', text: `It's getting more manageable. The breathing exercises before work have been a big help. Thank you for suggesting those.`, user: { id: patient._id, name: `${patient.firstName} ${patient.lastName}` }, created_at: ts(1440) },
    { id: 'demo-6', text: `I'm really glad to hear that. Progress isn't always linear — it's okay to have difficult days too. Looking forward to our session this week!`, user: { id: doctorId, name: drName }, created_at: ts(1435) },
    { id: 'demo-7', text: `Thank you, Doctor. I'll see you then!`, user: { id: patient._id, name: `${patient.firstName} ${patient.lastName}` }, created_at: ts(60) },
  ];
};

const selectPatient = async (patient) => {
  try {
    if (!doctorInfo) {
      console.error('❌ Doctor info not available');
      return;
    }

    if (!streamClient) {
      setSelectedPatient(patient);
      setMessages(getDemoMessages(patient, doctorInfo._id));
      setCurrentChannel(null);
      setError(null);
      return;
    }
    
    console.log('💬 Selecting patient for chat:', patient.firstName, patient.lastName);
    setSelectedPatient(patient);
    setMessages([]);
    setError(null);
    
    // ✅ Clear unread count for this patient
    setUnreadMessages(prev => {
      const newMap = new Map(prev);
      newMap.delete(patient._id);
      return newMap;
    });
    
    // Use the SAME channel ID format as Flutter
    const channelId = `doctor_${patient._id}_${doctorInfo._id}`;
    
    console.log('🔍 Using channel ID:', channelId);
    
    try {
      // ✅ First, try to query if channel exists WITHOUT creating it
      const existingChannels = await streamClient.queryChannels({
        type: 'messaging',
        id: channelId,
        members: { $in: [doctorInfo._id] }
      });
      
      let channel;
      
      if (existingChannels.length > 0) {
        // Channel exists - use it
        console.log('✅ Found existing channel');
        channel = existingChannels[0];
      } else {
        // Channel doesn't exist yet - create it but don't watch yet
        console.log('⚠️ No existing channel - creating placeholder');
        channel = streamClient.channel('messaging', channelId, {
          name: `Dr. ${doctorInfo.firstName} ${doctorInfo.lastName} & ${patient.firstName} ${patient.lastName}`,
          members: [doctorInfo._id, patient._id],
          created_by_id: doctorInfo._id,
          doctor_id: doctorInfo._id,
          patient_id: patient._id,
          channel_type: 'doctor_patient'
        });
        
        // DON'T watch yet - this will fail if patient user doesn't exist in Stream
        console.log('ℹ️ Channel created but not watched - waiting for patient to send first message');
      }
      
      // Try to watch the channel
      try {
        await channel.watch();
        setCurrentChannel(channel);
        
        // Load existing messages
        const state = await channel.query({
          messages: { limit: 50 }
        });
        
        const allMessages = state.messages || [];
        setMessages(allMessages.length > 0 ? allMessages : getDemoMessages(patient, doctorInfo._id));
        
        // 🎥 Extract session invitations from messages using new extraData format
        const sessionInvites = new Map();
        allMessages.forEach(message => {
          if (message.extraData?.message_type === 'video_session_invitation') {
            const data = message.extraData;
            sessionInvites.set(data.session_id, {
              sessionId: data.session_id,
              sessionTime: data.session_time,
              sessionDuration: data.session_duration,
              status: data.status,
              messageId: message.id
            });
          }
        });
        setSessionInvitations(sessionInvites);
        
        console.log('💬 Loaded', allMessages.length, 'messages');
        console.log('🎥 Found', sessionInvites.size, 'session invitations');
        
      
// Listen for new messages
channel.on('message.new', (event) => {
  console.log('📩 New message received:', event.message.text || event.message.type);
  setMessages(prev => [...prev, event.message]);
  
  // ✅ Track unread if message is from patient (not from doctor)
  if (event.message.user?.id !== doctorInfo._id) {
    console.log('📬 New unread message from patient');
    setUnreadMessages(prev => {
      const newMap = new Map(prev);
      const currentCount = newMap.get(patient._id) || 0;
      newMap.set(patient._id, currentCount + 1);
      return newMap;
    });
  }
  
  // ✅ NEW: Update patient's lastMessageTime and reorder the list
  setPatients(prevPatients => {
    const updatedPatients = prevPatients.map(p => {
      if (p._id === patient._id) {
        const messageText = event.message.text || '';
        const preview = messageText.length > 50 ? messageText.substring(0, 50) + '...' : messageText;
        
        return {
          ...p,
          lastMessageTime: event.message.created_at,
          lastMessagePreview: event.message.user?.id === doctorInfo._id 
            ? `You: ${preview}`
            : preview,
          lastMessageIsFromDoctor: event.message.user?.id === doctorInfo._id
        };
      }
      return p;
    });
    
    // Sort by most recent message
    return sortPatientsByLastMessage(updatedPatients);
  });
  
  // 🎥 Handle video session messages with new extraData format
  if (event.message.extraData?.message_type === 'video_session_invitation') {
    const data = event.message.extraData;
    setSessionInvitations(prev => new Map(prev.set(data.session_id, {
      sessionId: data.session_id,
      sessionTime: data.session_time,
      sessionDuration: data.session_duration,
      status: data.status,
      messageId: event.message.id
    })));
  }
});
        
      } catch (watchError) {
        // Channel exists but patient user not registered in Stream yet
        if (watchError.message?.includes('was not found') ||
            watchError.message?.includes('do not exist')) {
          console.log('ℹ️ Patient has not sent any messages yet');
          setCurrentChannel(null);
          setMessages(getDemoMessages(patient, doctorInfo._id));
          setError(null);
        } else {
          throw watchError; // Re-throw other errors
        }
      }
      
      // Scroll to bottom
      setTimeout(() => {
        scrollToBottom();
      }, 100);
      
    } catch (channelError) {
      console.error('❌ Error with channel:', channelError);

      if (channelError.message?.includes('was not found') ||
          channelError.message?.includes('do not exist')) {
        console.log('ℹ️ Waiting for patient to start conversation');
        setCurrentChannel(null);
        setMessages(getDemoMessages(patient, doctorInfo._id));
      } else {
        setCurrentChannel(null);
        setMessages(getDemoMessages(patient, doctorInfo._id));
        setError(null);
      }
    }
    
  } catch (error) {
    console.error('❌ Error selecting patient:', error);
    setMessages(getDemoMessages(patient, doctorInfo?._id));
    setError(null);
  }
};

  // 🎥 Create video session
  const createVideoSession = async (sessionData) => {
    try {
      if (!currentChannel || !selectedPatient) {
        throw new Error('No active chat selected');
      }

      console.log('🎥 Creating video session...', sessionData);

      const response = await fetch('/api/chat/session/create', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          channelId: currentChannel.id,
          patientId: selectedPatient._id,
          appointmentId: sessionData.appointmentId,
          sessionTime: sessionData.sessionTime,
          sessionDuration: sessionData.sessionDuration
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to create video session');
      }

      const result = await response.json();
      console.log('✅ Video session created:', result.data);

      // Close modal and reload upcoming sessions
      setShowCreateSessionModal(false);
      await loadUpcomingSessions();

      return result.data;
    } catch (error) {
      console.error('❌ Error creating video session:', error);
      setError(error.message);
      throw error;
    }
  };

  // 🎥 Join video session
  const joinVideoSession = async (sessionId) => {
    try {
      console.log('🎥 Attempting to join session:', sessionId);

      // Check if user can join the session
      const response = await fetch('/api/chat/session/join', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ sessionId })
      });

      const result = await response.json();

      if (!result.success) {
        setError(result.message);
        return;
      }

      // Update session status to starting
      await updateSessionStatus(sessionId, 'starting');

      // Set active video session (this will open the video overlay)
      setActiveVideoSession({
        sessionId,
        userRole: 'doctor',
        patientInfo: selectedPatient,
        doctorInfo: doctorInfo
      });

      console.log('✅ Joined video session successfully');
    } catch (error) {
      console.error('❌ Error joining video session:', error);
      setError('Failed to join video session. Please try again.');
    }
  };

  // 🎥 Update session status
  const updateSessionStatus = async (sessionId, status) => {
    try {
      const response = await fetch('/api/chat/session/status', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          sessionId,
          status,
          channelId: currentChannel?.id
        })
      });

      if (response.ok) {
        console.log('✅ Session status updated:', status);
      }
    } catch (error) {
      console.error('❌ Error updating session status:', error);
    }
  };

  // 🎥 End video session
  const endVideoSession = async () => {
    if (activeVideoSession) {
      await updateSessionStatus(activeVideoSession.sessionId, 'ended');
      setActiveVideoSession(null);
      console.log('✅ Video session ended');
    }
  };

  // 🎥 ENHANCED: Quick session handlers
 // ✅ FIXED: React (Doctor) Side - DoctorMessages.jsx
// In handleQuickSessionRequest function:

const handleQuickSessionRequest = async (message) => {
  try {
    console.log('🎥 ===== WEB: PROCESSING QUICK SESSION =====');
    console.log('🎥 Channel ID:', currentChannel.id);
    console.log('🎥 Patient:', selectedPatient._id);
    console.log('🎥 Doctor:', doctorInfo._id);
    
    // ✅ CRITICAL: Let backend create the deterministic session ID
    const sessionData = {
      sessionTime: new Date().toISOString(),
      sessionDuration: 30,
      appointmentId: null,
      sessionType: 'quick_session'  // ✅ Backend will create deterministic ID
    };
    
    console.log('🎥 Creating session via backend...');
    
    const response = await fetch('/api/chat/session/create', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      },
      body: JSON.stringify({
        channelId: currentChannel.id,
        patientId: selectedPatient._id,
        appointmentId: sessionData.appointmentId,
        sessionTime: sessionData.sessionTime,
        sessionDuration: sessionData.sessionDuration,
        sessionType: sessionData.sessionType
      })
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to create video session');
    }
    
    const createdSession = await response.json();
    console.log('🎥 ===== BACKEND RESPONSE =====');
    console.log(createdSession);
    
    // ✅ Get the session ID that backend created
    const backendSessionId = createdSession.data?.sessionId || createdSession.data?.session_id;
    
    if (!backendSessionId) {
      throw new Error('Backend did not return session ID');
    }
    
    console.log('🎥 ✅ BACKEND CREATED SESSION ID:', backendSessionId);
    console.log('🎥 ✅ MOBILE SHOULD DETECT THIS SAME ID FROM MESSAGES');
    
    // ✅ Doctor joins with backend's session ID
    setActiveVideoSession({
      sessionId: backendSessionId,  // ✅ Use backend's ID
      userRole: 'doctor',
      patientInfo: selectedPatient,
      doctorInfo: doctorInfo
    });
    
    console.log('🎥 ✅ DOCTOR JOINING WITH SESSION ID:', backendSessionId);
    
  } catch (error) {
    console.error('❌ Error in quick session:', error);
    setError('Failed to create quick session: ' + error.message);
  }
};

  const handleDeclineQuickSession = async (message) => {
    try {
      console.log('🎥 Declining quick session request...');
      
      // Send decline message
      await currentChannel.sendMessage({
        text: '❌ I\'m not available for a video session right now. Please schedule an appointment through the app for our next session.',
        user_id: doctorInfo._id,
        sender_type: 'doctor',
        extraData: {
          message_type: 'video_session_response',
          response_type: 'declined',
          original_message_id: message.id
        }
      });
      
      console.log('✅ Quick session request declined');
      
    } catch (error) {
      console.error('❌ Error declining quick session request:', error);
      setError('Failed to decline session request');
    }
  };

  // Enhanced sendMessage
  const sendMessage = async () => {
  if (!newMessage.trim() || !currentChannel || sending) return;
  
  try {
    setSending(true);
    
    // Send message via Stream Chat
    await currentChannel.sendMessage({
      text: newMessage.trim(),
      user_id: doctorInfo._id,
      sender_type: 'doctor'
    });
    
    // ✅ NEW: Update patient list order after sending
    setPatients(prevPatients => {
      const updatedPatients = prevPatients.map(p => {
        if (p._id === selectedPatient._id) {
          const preview = newMessage.trim().substring(0, 50);
          return {
            ...p,
            lastMessageTime: new Date().toISOString(),
            lastMessagePreview: `You: ${preview}${newMessage.trim().length > 50 ? '...' : ''}`,
            lastMessageIsFromDoctor: true
          };
        }
        return p;
      });
      
      // Sort by most recent message
      return sortPatientsByLastMessage(updatedPatients);
    });
    
    // Trigger FCM notification to mobile user
    try {
      console.log('🔔 Triggering mobile notification...');
      
      const response = await fetch('/api/notifications/message', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          recipientId: selectedPatient._id,
          messageContent: newMessage.trim(),
          conversationId: currentChannel.id
        })
      });
      
      if (response.ok) {
        const result = await response.json();
        console.log('✅ FCM notification triggered successfully!', result);
      } else {
        const errorText = await response.text();
        console.error('❌ Failed to trigger FCM notification:', response.status, errorText);
      }
    } catch (notificationError) {
      console.error('❌ Error triggering notification:', notificationError);
    }
    
    setNewMessage('');
    messageInputRef.current?.focus();
    
  } catch (error) {
    console.error('❌ Error sending message:', error);
    setError('Failed to send message. Please try again.');
  } finally {
    setSending(false);
  }
};

  // Utility functions
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const formatMessageTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMinutes = Math.floor((now - date) / (1000 * 60));
    
    if (diffMinutes < 1) return 'Just now';
    if (diffMinutes < 60) return `${diffMinutes}m ago`;
    if (diffMinutes < 1440) return `${Math.floor(diffMinutes / 60)}h ago`;
    
    return date.toLocaleDateString();
  };

  const getPatientInitials = (patient) => {
    return `${patient.firstName?.charAt(0) || ''}${patient.lastName?.charAt(0) || ''}`.toUpperCase();
  };

  const getDoctorInitials = () => {
    if (!doctorInfo) return 'DR';
    return `${doctorInfo.firstName?.charAt(0) || ''}${doctorInfo.lastName?.charAt(0) || ''}`.toUpperCase();
  };

  // 🎥 Check if session can be joined
  const canJoinSession = (sessionTime) => {
    const now = new Date();
    const sessionDate = new Date(sessionTime);
    const minutesUntilSession = Math.floor((sessionDate - now) / (1000 * 60));
    
    return minutesUntilSession <= 5 && minutesUntilSession >= -15;
  };

  if (loading) {
  return (
    <FeatureWrapper feature="Messages" showMessage={true}>
      <div className="neurolex-loading-container">
        <div className="neurolex-loading-insideContainer">
        <div className="neurolex-dashboard-spinner">
          <div className="spinner-circle outer"></div>
          <div className="spinner-circle middle"></div>
          <div className="spinner-circle inner"></div>
          <div className="spinner-center">
            <img 
              src={neurolexLogo} 
              alt="NeuroLex Logo" 
              className="loading-logo"
              onError={(e) => {
                console.warn('❌ Logo failed to load from:', neurolexLogo);
                // Fallback to messages emoji if logo doesn't load
                e.target.style.display = 'none';
                const fallback = e.target.nextElementSibling;
                if (fallback) {
                  fallback.style.display = 'flex';
                  fallback.style.alignItems = 'center';
                  fallback.style.justifyContent = 'center';
                }
              }}
              onLoad={() => {
                console.log('✅ NeuroLex logo loaded successfully:', neurolexLogo);
              }}
            />
            <div className="loading-logo-fallback" style={{ display: 'none', alignItems: 'center', justifyContent: 'center' }}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#274137" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
            </div>
          </div>
        </div>
        </div>
      </div>
    </FeatureWrapper>
  );
}

  return (
    <FeatureWrapper feature="Messages" showMessage={true}>
      <div className="doctor-messages-container">
        {error && (
          <div className="error-banner">
            <span className="error-icon"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg></span>
            <span className="error-text">{error}</span>
            <button 
              className="error-dismiss"
              onClick={() => setError(null)}
            >
              ×
            </button>
          </div>
        )}

        {/* Sidebar Container */}
        <div className="sidebar-container">
          {/* Enhanced Header */}
            <div className="messages-header">
              <div className="header-left">
                <h1 className="messages-title">Messages</h1>
                <p className="messages-subtitle">
                  Where conversations and care continue.
                </p>
              </div>
              <div className="header-right">
                <img 
                  src={journalIconUrl} 
                  alt="Message Indicator" 
                  className="message-indicator-icon"
                />
              </div>
            </div>

          {/* Search Section */}
          <div className="search-section">
            <div className="search-container">
              <input
                type="text"
                placeholder="Search patients..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="patient-search"
              />
            </div>
          </div>

         {/* Conversations List */}
<div className="conversations-list">
  {filteredPatients.length === 0 ? (
    <div className="no-patients">
      <p className="no-patients-text">
        {searchTerm ? 'No patients found matching your search.' : 'No patients with appointments found.'}
      </p>
      {!searchTerm && (
        <p className="no-patients-subtitle">
          Patients will appear here after they book appointments with you.
        </p>
      )}
    </div>
  ) : (
    filteredPatients.map((patient) => {
      const unreadCount = unreadMessages.get(patient._id) || 0;
      const hasUnread = unreadCount > 0;
      
      return (
        <div
          key={patient._id}
          className={`conversation-item ${selectedPatient?._id === patient._id ? 'selected' : ''} ${hasUnread ? 'has-unread' : ''}`}
          onClick={() => selectPatient(patient)}
        >
          {hasUnread && <div className="new-message-indicator"></div>}
          
          <div className="conversation-avatar">
            {patient.profilePicture && patient.profilePicture !== 'default-profile.png' && patient.profilePicture.startsWith('http') ? (
              <img src={patient.profilePicture} alt={`${patient.firstName} ${patient.lastName}`} />
            ) : (
              <div className="avatar-initials">{getPatientInitials(patient)}</div>
            )}
            <div className="online-indicator"></div>
          </div>
          
          <div className="conversation-info">
            <div className="conversation-header">
              <div className="patient-name">
                {patient.firstName} {patient.lastName}
              </div>
              <div className="last-message-time">
                {patient.lastMessageTime 
                  ? formatMessageTime(patient.lastMessageTime) 
                  : patient.lastAppointment 
                    ? formatMessageTime(patient.lastAppointment)
                    : 'New'
                }
              </div>
            </div>
            
            {/* ✅ NEW: Last message preview instead of appointment count */}
            <div className="conversation-preview">
              <span className={`last-message-preview ${hasUnread ? 'has-unread-text' : ''}`}>
                {patient.lastMessagePreview || 'No messages yet'}
              </span>
              {patient.nextAppointment && (
                <span className="next-session-indicator">
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{verticalAlign:'middle',marginRight:'3px'}}><polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2" ry="2"/></svg>
                  {new Date(patient.nextAppointment.appointmentDate).toLocaleDateString()}
                </span>
              )}
            </div>
          </div>
          
          <div className="conversation-badge">
            {hasUnread && (
              <div className="unread-badge">
                <span className="unread-badge-text">{unreadCount}</span>
              </div>
            )}
          </div>
        </div>
      );
    })
  )}
</div>
</div>
        {/* Chat Area */}
        <div className="chat-area">
          {selectedPatient ? (
            <>
              {/* Enhanced Chat Header with video session button */}
              <div className="chat-header">
                <div className="chat-patient-info">
                  <div className="chat-patient-avatar">
                    {selectedPatient.profilePicture && selectedPatient.profilePicture !== 'default-profile.png' && selectedPatient.profilePicture.startsWith('http') ? (
                      <img src={selectedPatient.profilePicture} alt={`${selectedPatient.firstName} ${selectedPatient.lastName}`} />
                    ) : (
                      <div className="avatar-initials">{getPatientInitials(selectedPatient)}</div>
                    )}
                  </div>
                  <div className="chat-patient-details">
                    <h3 className="chat-patient-name">
                      {selectedPatient.firstName} {selectedPatient.lastName}
                    </h3>
                    <p className="chat-patient-status">
                      {selectedPatient.appointmentCount} appointment{selectedPatient.appointmentCount !== 1 ? 's' : ''}
                    </p>
                  </div>
                </div>
              </div>

              {/* Enhanced Messages with video session support */}
              <div className="messages-container">
                {messages.length === 0 ? (
  <div className="no-messages">
    <div className="no-messages-icon"><svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg></div>
    {currentChannel ? (
      <>
        <h3 className="no-messages-title">Start the conversation</h3>
        <p className="no-messages-text">
          Send a message to {selectedPatient.firstName} to begin your secure conversation.
        </p>
      </>
    ) : (
      <>
        <h3 className="no-messages-title">Waiting for {selectedPatient.firstName} to start</h3>
        <p className="no-messages-text">
          {selectedPatient.firstName} needs to send the first message from their mobile app to start the conversation.
          Once they do, you'll be able to chat here.
        </p>
      </>
    )}
  </div>
                ) : (
                  <div className="messages-list">
                    {messages.map((message, index) => {
                      const isDoctor = message.user?.id === doctorInfo._id;
                      const isConsecutive = index > 0 && 
                        messages[index - 1].user?.id === message.user?.id &&
                        (new Date(message.created_at) - new Date(messages[index - 1].created_at)) < 60000;
                      
                      // 🔍 Enhanced DEBUG LOGGING for message detection
                      console.log('🎥 Message Debug:', {
                        text: message.text,
                        type: message.type,
                        extraData: message.extraData,
                        attachments: message.attachments,
                        isDoctor: isDoctor,
                        userId: message.user?.id,
                        doctorId: doctorInfo._id,
                        detectsAsQuickSession: detectQuickSessionRequest(message)
                      });
                      
                      // 🎥 PRIORITY 1: Check for video session invitation with extraData
                      if (message.extraData?.message_type === 'video_session_invitation') {
                        console.log('🎥 Video session invitation detected:', message.extraData);
                        
                        const sessionData = message.extraData;
                        
                        if (sessionData.session_id && sessionData.session_time) {
                          return (
                            <VideoSessionInvitation
                              key={message.id}
                              sessionId={sessionData.session_id}
                              sessionTime={sessionData.session_time}
                              sessionDuration={parseInt(sessionData.session_duration) || 60}
                              status={sessionData.status || 'scheduled'}
                              canJoin={canJoinSession(sessionData.session_time)}
                              onJoinSession={joinVideoSession}
                              userRole="doctor"
                            />
                          );
                        } else {
                          console.error('❌ Missing required session data:', sessionData);
                          return (
                            <div key={message.id} className="error-message">
                              <span><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{verticalAlign:'middle',marginRight:'4px'}}><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg> Invalid video session invitation</span>
                            </div>
                          );
                        }
                      }
                      
                      // 🎥 PRIORITY 2: Check for video session status messages
                      if (message.extraData?.message_type === 'video_session_status') {
                        return (
                          <div key={message.id} className="session-status-message">
                            <div className="session-status-content">
                              <span className="session-status-icon"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2" ry="2"/></svg></span>
                              <span className="session-status-text">{message.text}</span>
                              <span className="session-status-time">
                                {formatMessageTime(message.created_at)}
                              </span>
                            </div>
                          </div>
                        );
                      }
                      
                      // 🎥 PRIORITY 3: Check for quick session requests (TEXT-BASED)
                      if (detectQuickSessionRequest(message)) {
                        console.log('🎥 Quick session request detected - rendering special UI');
                        return (
                          <QuickSessionRequestMessage
                            key={message.id}
                            message={message}
                            onAccept={handleQuickSessionRequest}
                            onDecline={handleDeclineQuickSession}
                          />
                        );
                      }
                      
                      // 🎥 PRIORITY 4: Regular message rendering
                      return (
                        <div
                          key={message.id}
                          className={`message ${isDoctor ? 'doctor-message' : 'patient-message'} ${isConsecutive ? 'consecutive' : ''}`}
                        >
                          {!isConsecutive && (
                            <div className="message-avatar">
                              {isDoctor ? (
                                (currentUser?.profilePicture && currentUser.profilePicture !== 'default-profile.png' && currentUser.profilePicture.startsWith('http')) || (doctorInfo?.profilePicture && doctorInfo.profilePicture !== 'default-profile.png' && doctorInfo.profilePicture.startsWith('http')) ? (
                                  <img
                                    src={currentUser?.profilePicture || doctorInfo?.profilePicture}
                                    alt="Doctor"
                                    className="message-avatar-image"
                                    style={{ width: '28.65px', height: '28.65px', borderRadius: '50%', objectFit: 'cover' }}
                                  />
                                ) : (
                                  <div className="avatar-initials doctor-avatar">
                                    {getDoctorInitials()}
                                  </div>
                                )
                              ) : (
                                selectedPatient.profilePicture && selectedPatient.profilePicture !== 'default-profile.png' && selectedPatient.profilePicture.startsWith('http') ? (
                                  <img src={selectedPatient.profilePicture} alt={selectedPatient.firstName} />
                                ) : (
                                  <div className="avatar-initials">{getPatientInitials(selectedPatient)}</div>
                                )
                              )}
                            </div>
                          )}
                          <div className="message-content">
                            {!isConsecutive && (
                              <div className="message-header">
                                <span className="message-time">
                                  {formatMessageTime(message.created_at)}
                                </span>
                              </div>
                            )}
                            <div className="message-text">
                              {message.text}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    <div ref={messagesEndRef} />
                  </div>
                )}
              </div>

              {/* Message Input */}
              <div className="message-input-container">
                <div className="message-input-wrapper">
                  <textarea
                    ref={messageInputRef}
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder={`Message ${selectedPatient.firstName}...`}
                    className="message-input"
                    rows="1"
                    disabled={sending}
                  />
                  <button
                    onClick={sendMessage}
                    disabled={!newMessage.trim() || sending}
                    className="send-button"
                    style={{ backgroundColor: '#A7D7C5' }}
                  >
                    {sending ? '' : 'Send'}
                  </button>
                </div>
                <div className="message-input-hint">
                  Press Enter to send, Shift+Enter for new line
                </div>
              </div>
            </>
          ) : (
            <div className="no-chat-selected">
              <div className="no-chat-icon"><svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg></div>
              <h3 className="no-chat-title">Select a patient to start messaging</h3>
              <p className="no-chat-text">
                Choose a patient from the list to begin a secure conversation through {platformName}.
              </p>
            </div>
          )}
        </div>

        {/* 🎥 VIDEO SESSION MODALS AND OVERLAYS */}
        {showCreateSessionModal && selectedPatient && (
          <CreateSessionModal
            patient={selectedPatient}
            onClose={() => setShowCreateSessionModal(false)}
            onCreateSession={createVideoSession}
          />
        )}

        {activeVideoSession && (
          <VideoSessionOverlay
            sessionId={activeVideoSession.sessionId}
            userRole={activeVideoSession.userRole}
            patientInfo={activeVideoSession.patientInfo}
            doctorInfo={activeVideoSession.doctorInfo}
            onEndSession={endVideoSession}
            onUpdateStatus={updateSessionStatus}
          />
        )}
      </div>
    </FeatureWrapper>
  );
};

export default DoctorMessages;